import {useCallback} from "react";
import {SignerTransaction} from "@tinymanorg/tinyman-js-sdk";

import {
  useSelectedAccountAddress,
  useSelectedWalletType,
  useWalletContextDispatch
} from "../../../wallet/util/context/WalletContext.utils";
import {initiateSignCeremony} from "../../integrations/tinyman-js-sdk/tinymanHelpers";

/**
 * @param signerAddress Should be provided if the signer is not the selected account
 * @returns a callback that initiates a sign ceremony using the selected wallet (or the given `signerAddress`).
 */
function useInitiateSignCeremony(signerAddress?: string) {
  const walletContextDispatch = useWalletContextDispatch();
  const selectedAccountAddress = useSelectedAccountAddress();
  const {walletType, isPeraWallet} = useSelectedWalletType();
  const accountAddress = (signerAddress ?? selectedAccountAddress) || null;

  return {
    signerCallback: useCallback(
      (txGroups: SignerTransaction[][]) => {
        if (!walletType) {
          throw new Error("Signing is attempted, but the wallet type is unavailable.");
        }

        const promise = initiateSignCeremony(
          {walletType, signerAddress: accountAddress},
          txGroups
        );

        promise.catch((error: any) => {
          /**
           * this error is handled within the component where useInitiatorSigner is used.
           * but there's a special case where the user disconnects their wallet on Pera Web.
           * in this case, we want to capture disconnect errors and disconnect the user.
           * https://github.com/perawallet/connect/blob/main/src/util/PeraWalletConnectError.ts
           */
          if (isPeraWallet && error.data.type === "SESSION_DISCONNECTED") {
            walletContextDispatch({
              type: "DISCONNECT"
            });
          }

          throw error;
        });

        return promise;
      },
      [walletType, accountAddress, isPeraWallet, walletContextDispatch]
    )
  };
}

export type InitiatorSignerCallback = ReturnType<
  typeof useInitiateSignCeremony
>["signerCallback"];

export default useInitiateSignCeremony;
